import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Drawer,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { LuArrowLeft, LuMessageCircle, LuSend } from 'react-icons/lu'
import { useSession } from '@/frontend/hooks/useAuth'
import {
  type ChatMessage,
  useChatWebSocket,
  useConversations,
  useMarkRead,
  useMessages,
  useSendMessage,
} from '@/frontend/hooks/useChat'
import { CustomerAvatar } from './CustomerAvatar'

export function LiveChatBubble() {
  const [opened, handlers] = useDisclosure(false)
  const { data } = useConversations()
  const conversations = data?.items ?? []
  const totalUnread = conversations.reduce((s, c) => s + c.unreadCount, 0)

  const [activeId, setActiveId] = useState<string | null>(null)
  const active = conversations.find((c) => c.id === activeId)
  const myId = useMyUserId()

  return (
    <>
      <Tooltip label="Messages" position="left">
        <ActionIcon
          size={48}
          radius="xl"
          variant="filled"
          color="karang"
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            zIndex: 100,
          }}
          onClick={handlers.open}
          aria-label="Live chat"
        >
          <LuMessageCircle size={22} />
          {totalUnread > 0 && (
            <Badge
              size="xs"
              color="red"
              circle
              style={{ position: 'absolute', top: -4, right: -4, padding: 0, minWidth: 18, height: 18 }}
            >
              {totalUnread}
            </Badge>
          )}
        </ActionIcon>
      </Tooltip>

      <Drawer
        position="right"
        size={420}
        opened={opened}
        onClose={() => {
          handlers.close()
          setActiveId(null)
        }}
        padding={0}
        withCloseButton={false}
        styles={{ body: { padding: 0, height: '100%' } }}
      >
        {!active ? (
          <ConversationListView
            conversations={conversations}
            myId={myId}
            onOpen={(id) => setActiveId(id)}
            onClose={handlers.close}
          />
        ) : (
          <ConversationThread
            conversationId={active.id}
            title={conversationTitle(active, myId)}
            onBack={() => setActiveId(null)}
          />
        )}
      </Drawer>
    </>
  )
}

function useMyUserId() {
  const { data } = useSession()
  return data?.user?.id ?? ''
}

function conversationTitle(
  c: { title: string | null; participants: { user: { id: string; name: string } }[] },
  myId: string,
): string {
  if (c.title) return c.title
  const others = c.participants.filter((p) => p.user.id !== myId).map((p) => p.user.name)
  return others.join(', ') || 'Direct message'
}

function ConversationListView({
  conversations,
  myId,
  onOpen,
  onClose,
}: {
  conversations: any[]
  myId: string
  onOpen: (id: string) => void
  onClose: () => void
}) {
  return (
    <Stack gap={0} h="100%">
      <Group
        justify="space-between"
        px="md"
        py="sm"
        style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}
      >
        <Title order={4}>Messages</Title>
        <Button variant="subtle" size="xs" onClick={onClose}>
          Close
        </Button>
      </Group>
      <ScrollArea style={{ flex: 1 }}>
        {conversations.length === 0 ? (
          <Stack align="center" gap="sm" p="xl">
            <Text c="dimmed" ta="center" size="sm">
              No conversations yet.
            </Text>
            <Text c="dimmed" ta="center" size="xs">
              Start conversations via API `POST /api/chat/conversations` — full UI untuk create/invite coming Phase 13+.
            </Text>
          </Stack>
        ) : (
          <Stack gap={0}>
            {conversations.map((c) => {
              const title = conversationTitle(c, myId)
              return (
                <Box
                  key={c.id}
                  px="md"
                  py="sm"
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--mantine-color-default-border)',
                  }}
                  onClick={() => onOpen(c.id)}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                      <CustomerAvatar name={title} size={36} />
                      <Stack gap={0} style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>
                          {title}
                        </Text>
                        <Text size="xs" c="dimmed" truncate>
                          {c.lastMessage?.body ?? 'No messages yet'}
                        </Text>
                      </Stack>
                    </Group>
                    {c.unreadCount > 0 && (
                      <Badge color="red" circle>
                        {c.unreadCount}
                      </Badge>
                    )}
                  </Group>
                </Box>
              )
            })}
          </Stack>
        )}
      </ScrollArea>
    </Stack>
  )
}

function ConversationThread({
  conversationId,
  title,
  onBack,
}: {
  conversationId: string
  title: string
  onBack: () => void
}) {
  const myId = useMyUserId()
  const qc = useQueryClient()
  const { data } = useMessages(conversationId)
  const messages = data?.messages ?? []
  const send = useSendMessage()
  const markRead = useMarkRead()
  const [body, setBody] = useState('')
  const viewportRef = useRef<HTMLDivElement | null>(null)

  const ws = useChatWebSocket((msg) => {
    if (msg.type === 'message' && msg.message.conversationId === conversationId) {
      qc.setQueryData<{ messages: ChatMessage[] }>(['chat', 'messages', conversationId], (old) => ({
        messages: [...(old?.messages ?? []), msg.message],
      }))
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] })
    }
  })

  useEffect(() => {
    const timeout = setTimeout(() => {
      ws.subscribe(conversationId)
      markRead.mutate(conversationId)
    }, 200)
    return () => {
      clearTimeout(timeout)
      ws.unsubscribe(conversationId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, markRead.mutate, ws.unsubscribe, ws.subscribe])

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = viewportRef.current.scrollHeight
    }
  }, [])

  const handleSend = () => {
    if (!body.trim()) return
    send.mutate({ conversationId, data: { body: body.trim() } })
    setBody('')
  }

  return (
    <Stack gap={0} h="100%">
      <Group gap="sm" px="md" py="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <ActionIcon variant="subtle" onClick={onBack} aria-label="Back">
          <LuArrowLeft size={16} />
        </ActionIcon>
        <CustomerAvatar name={title} size={32} />
        <Text fw={600} size="sm" truncate style={{ flex: 1 }}>
          {title}
        </Text>
      </Group>

      <ScrollArea viewportRef={viewportRef} style={{ flex: 1 }} p="md">
        <Stack gap="sm">
          {messages.length === 0 && (
            <Text c="dimmed" size="sm" ta="center">
              No messages yet
            </Text>
          )}
          {messages.map((m) => {
            const mine = m.senderUserId === myId
            return (
              <Group key={m.id} justify={mine ? 'flex-end' : 'flex-start'} gap="xs" wrap="nowrap" align="flex-end">
                {!mine && <CustomerAvatar name={m.sender?.name} size={28} />}
                <Paper
                  p="xs"
                  px="sm"
                  radius="md"
                  style={{
                    maxWidth: '70%',
                    background: mine ? 'var(--mantine-color-teal-5)' : 'var(--mantine-color-gray-1)',
                    color: mine ? 'white' : undefined,
                  }}
                >
                  {!mine && m.sender && (
                    <Text size="xs" fw={600} mb={2}>
                      {m.sender.name}
                    </Text>
                  )}
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {m.body}
                  </Text>
                  <Text size="xs" c={mine ? 'teal.1' : 'dimmed'} mt={2} ta="right">
                    {dayjs(m.createdAt).format('HH:mm')}
                  </Text>
                </Paper>
              </Group>
            )
          })}
        </Stack>
      </ScrollArea>

      <Group gap="xs" p="sm" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
        <Textarea
          placeholder="Type a message…"
          autosize
          minRows={1}
          maxRows={4}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          style={{ flex: 1 }}
        />
        <ActionIcon
          size="lg"
          color="laut"
          variant="filled"
          onClick={handleSend}
          loading={send.isPending}
          aria-label="Send"
        >
          <LuSend size={16} />
        </ActionIcon>
      </Group>
    </Stack>
  )
}
